import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useForm } from 'react-hook-form';
import axios from 'axios';
import {
  Box,
  Button,
  CircularProgress,
  Modal,
  TextField,
  Typography,
  Fade,
  IconButton,
  InputAdornment,
  Grow
} from '@mui/material';
import { Visibility, VisibilityOff, Lock, Email, Dashboard, BarChart, Security, Support } from '@mui/icons-material';

// Nova paleta de cores baseada no solicitado (#5AB9D3 e #101A3A)
const colors = {
  primary: '#5AB9D3',       // Azul claro
  primaryDark: '#4AA5C0',   // Tom mais escuro do azul claro
  secondary: '#101A3A',     // Azul escuro
  secondaryLight: '#1A2747', // Tom mais claro do azul escuro
  backgroundLight: '#F8FAFC',
  textDark: '#2D3748',
  textLight: '#718096',
  error: '#E53E3E',
  success: '#38A169',
  white: '#FFFFFF'
};

// Estilos atualizados com a nova paleta
const Container = styled.div`
  display: flex;
  min-height: 100vh;
  width: 100%;
  background: ${colors.backgroundLight};
  overflow: hidden;
  position: relative;

  @media (max-width: 900px) {
    flex-direction: column;
  }
`;

const LeftSection = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem;
  position: relative;
  z-index: 1;
  animation: fadeIn 0.8s ease-in-out;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 100px;
    height: 100%;
    background: linear-gradient(90deg, rgba(248,250,252,0) 0%, ${colors.backgroundLight} 100%);
    z-index: 2;

    @media (max-width: 900px) {
      display: none;
    }
  }
`;

const RightSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 4rem 2rem;
  background: linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%);
  color: ${colors.white};
  position: relative;
  overflow: hidden;
  animation: fadeIn 0.8s ease-in-out;

  &::after {
    content: '';
    position: absolute;
    top: -50%;
    right: -50%;
    width: 100%;
    height: 200%;
    background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%);
    transform: rotate(30deg);
  }

  @media (max-width: 900px) {
    padding: 2rem;
    display: none;
  }
`;

const BrandLogo = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 2rem;
  font-size: 1.5rem;
  font-weight: 700;
  color: ${colors.primary};

  svg {
    margin-right: 0.5rem;
    font-size: 2rem;
  }
`;

const Inner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 450px;
  position: relative;
  z-index: 3;
  animation: slideUp 0.6s ease-in-out;

  @keyframes slideUp {
    from { transform: translateY(20px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 2rem;
  width: 100%;
`;

const Title = styled.h1`
  font-size: 2.2rem;
  color: ${colors.secondary};
  margin-bottom: 0.5rem;
  font-weight: 700;
  background: linear-gradient(to right, ${colors.primary}, ${colors.secondary});
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Subtitle = styled.h2`
  font-size: 1rem;
  color: ${colors.textLight};
  margin-bottom: 2rem;
  font-weight: 400;
  line-height: 1.6;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 1.2rem;
`;

const StyledTextField = styled(TextField)`
  && {
    .MuiOutlinedInput-root {
      border-radius: 12px;
      background-color: rgba(255, 255, 255, 0.9);
      
      &:hover fieldset {
        border-color: ${colors.primary};
      }
      
      &.Mui-focused fieldset {
        border-color: ${colors.primary};
        border-width: 2px;
      }
    }
    
    .MuiInputLabel-root {
      color: ${colors.textLight};
      
      &.Mui-focused {
        color: ${colors.primary};
      }
    }
  }
`;

const ForgotLink = styled.a`
  color: ${colors.primary};
  text-decoration: none;
  font-size: 0.9rem;
  text-align: right;
  margin-top: -0.8rem;
  transition: all 0.2s;
  font-weight: 500;
  
  &:hover {
    color: ${colors.primaryDark};
    text-decoration: underline;
  }
`;

const StyledButton = styled(Button)`
  && {
    padding: 14px 24px;
    font-size: 1rem;
    font-weight: 600;
    border-radius: 12px;
    letter-spacing: 0.5px;
    transition: all 0.3s ease;
    background: linear-gradient(to right, ${colors.primary}, ${colors.secondary});
    color: ${colors.white};
    box-shadow: 0 4px 15px rgba(90, 185, 211, 0.3);
    text-transform: none;
    height: 48px;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(90, 185, 211, 0.4);
      background: linear-gradient(to right, ${colors.primaryDark}, ${colors.secondaryLight});
    }
    
    &:disabled {
      background: #CBD5E0;
    }
  }
`;

const BrandOverlay = styled.div`
  max-width: 500px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
`;

const Slogan = styled.h2`
  font-size: 2.2rem;
  color: ${colors.white};
  margin-bottom: 1.5rem;
  font-weight: 600;
  line-height: 1.3;
  animation: slideUp 0.6s ease-in-out;
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  margin-top: 3rem;
  width: 100%;
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  color: rgba(255, 255, 255, 0.9);
  font-size: 1rem;
  font-weight: 500;
  animation: slideUp 0.6s ease-in-out;
  animation-delay: ${props => props.delay || '0s'};
`;

const FeatureIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  
  svg {
    font-size: 1.2rem;
    color: ${colors.white};
  }
`;

const DecoratorCircle = styled.div`
  position: absolute;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.05);
  z-index: 1;
  
  &:nth-child(1) {
    width: 300px;
    height: 300px;
    top: -100px;
    right: -100px;
  }
  
  &:nth-child(2) {
    width: 150px;
    height: 150px;
    bottom: 50px;
    left: -50px;
  }
`;

// Componente de Login
export default function Login() {
  const { register, handleSubmit, formState: { errors } } = useForm();
  const [loading, setLoading] = useState(false);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [shake, setShake] = useState(false);
  const navigate = useNavigate();

  const handleCloseModal = () => setErrorModalOpen(false);
  const togglePasswordVisibility = () => setShowPassword(!showPassword);

  const onSubmit = async (data) => {
    setLoading(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/auth/login`,
        {
          email: data.email,
          password: data.password,
        }
      );

      const user = response.data;

      if (user?.token) {
        localStorage.setItem('token', user.token);
        localStorage.setItem('user', JSON.stringify(user));
        navigate('/dashboard');
      } else {
        throw new Error('Token não encontrado na resposta.');
      }
    } catch (error) {
      let message = 'Erro ao realizar login';

      if (axios.isAxiosError(error)) {
        message = error.response?.data?.message || error.message;
      } else if (error instanceof Error) {
        message = error.message;
      }
      
      setErrorMessage(message);
      setErrorModalOpen(true);
      setShake(true);
      setTimeout(() => setShake(false), 500);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      {/* Seção Esquerda: Formulário de Login */}
      <LeftSection>
        <Inner>
          <BrandLogo>
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M16 32C24.8366 32 32 24.8366 32 16C32 7.16344 24.8366 0 16 0C7.16344 0 0 7.16344 0 16C0 24.8366 7.16344 32 16 32Z" fill="url(#paint0_linear)"/>
              <path d="M22.4 12.8C22.4 14.92 20.52 16.8 18.4 16.8C16.28 16.8 14.4 14.92 14.4 12.8C14.4 10.68 16.28 8.8 18.4 8.8C20.52 8.8 22.4 10.68 22.4 12.8Z" fill="white"/>
              <path d="M9.6 19.2C9.6 21.32 7.72 23.2 5.6 23.2C3.48 23.2 1.6 21.32 1.6 19.2C1.6 17.08 3.48 15.2 5.6 15.2C7.72 15.2 9.6 17.08 9.6 19.2Z" fill="white"/>
              <path d="M26.4 19.2C26.4 21.32 24.52 23.2 22.4 23.2C20.28 23.2 18.4 21.32 18.4 19.2C18.4 17.08 20.28 15.2 22.4 15.2C24.52 15.2 26.4 17.08 26.4 19.2Z" fill="white"/>
              <defs>
                <linearGradient id="paint0_linear" x1="0" y1="0" x2="32" y2="32" gradientUnits="userSpaceOnUse">
                  <stop stopColor={colors.primary}/>
                  <stop offset="1" stopColor={colors.secondary}/>
                </linearGradient>
              </defs>
            </svg>
            Realoca Fácil
          </BrandLogo>

          <Header>
            <Title>Acesse sua conta</Title>
            <Subtitle>
              Gerencie seus clientes e processos com a plataforma mais completa do mercado
            </Subtitle>
          </Header>

          <Form onSubmit={handleSubmit(onSubmit)}>
            <StyledTextField
              id="email"
              label="Email"
              variant="outlined"
              type="email"
              fullWidth
              margin="normal"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email color="action" />
                  </InputAdornment>
                ),
              }}
              {...register('email', { 
                required: 'O email é obrigatório',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Email inválido'
                }
              })}
              error={!!errors.email}
              helperText={errors.email?.message}
              InputLabelProps={{ shrink: true }}
            />

            <StyledTextField
              id="password"
              label="Senha"
              variant="outlined"
              type={showPassword ? 'text' : 'password'}
              fullWidth
              margin="normal"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={togglePasswordVisibility}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              {...register('password', { 
                required: 'A senha é obrigatória',
                minLength: {
                  value: 6,
                  message: 'A senha deve ter pelo menos 6 caracteres'
                }
              })}
              error={!!errors.password}
              helperText={errors.password?.message}
              InputLabelProps={{ shrink: true }}
            />

            <ForgotLink href="/forgot-password">
              Esqueceu sua senha?
            </ForgotLink>

            <StyledButton
              type="submit"
              variant="contained"
              disabled={loading}
              fullWidth
              style={{ 
                transform: shake ? 'translateX(10px)' : 'translateX(0)',
                transition: 'transform 0.5s'
              }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Acessar Sistema'
              )}
            </StyledButton>
          </Form>
        </Inner>
      </LeftSection>

      {/* Seção Direita: Branding */}
      <RightSection>
        <DecoratorCircle />
        <DecoratorCircle />
        
        <BrandOverlay>
          <Fade in={true} timeout={1000}>
            <div>
              <Slogan>
                Transforme sua gestão de clientes
              </Slogan>
              
              <Typography variant="body1" style={{ 
                marginBottom: '2rem', 
                lineHeight: '1.6',
                fontSize: '1.1rem',
                opacity: 0.9,
                color: colors.white
              }}>
                A plataforma completa para otimizar seus processos e aumentar sua produtividade.
              </Typography>
              
              <FeaturesGrid>
                <Grow in={true} timeout={1000} style={{ transitionDelay: '100ms' }}>
                  <FeatureItem delay="0.1s">
                    <FeatureIcon><Dashboard /></FeatureIcon>
                    <span>Dashboard intuitivo</span>
                  </FeatureItem>
                </Grow>
                <Grow in={true} timeout={1000} style={{ transitionDelay: '200ms' }}>
                  <FeatureItem delay="0.2s">
                    <FeatureIcon><BarChart /></FeatureIcon>
                    <span>Relatórios avançados</span>
                  </FeatureItem>
                </Grow>
                <Grow in={true} timeout={1000} style={{ transitionDelay: '300ms' }}>
                  <FeatureItem delay="0.3s">
                    <FeatureIcon><Security /></FeatureIcon>
                    <span>Segurança de dados</span>
                  </FeatureItem>
                </Grow>
                <Grow in={true} timeout={1000} style={{ transitionDelay: '400ms' }}>
                  <FeatureItem delay="0.4s">
                    <FeatureIcon><Support /></FeatureIcon>
                    <span>Suporte especializado</span>
                  </FeatureItem>
                </Grow>
              </FeaturesGrid>
            </div>
          </Fade>
        </BrandOverlay>
      </RightSection>

      {/* Modal de Erro */}
      <Modal
        open={errorModalOpen}
        onClose={handleCloseModal}
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
        closeAfterTransition
      >
        <Fade in={errorModalOpen}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 400,
              bgcolor: 'background.paper',
              boxShadow: 24,
              p: 4,
              borderRadius: 3,
              outline: 'none',
              textAlign: 'center'
            }}
          >
            <Box
              sx={{
                width: 60,
                height: 60,
                borderRadius: '50%',
                bgcolor: '#FEE2E2',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1rem'
              }}
            >
              <Lock color="error" fontSize="large" />
            </Box>
            <Typography id="modal-title" variant="h6" component="h2" color="textPrimary" gutterBottom>
              Erro ao realizar login
            </Typography>
            <Typography id="modal-description" sx={{ mt: 1, mb: 2, color: colors.textLight }}>
              {errorMessage}
            </Typography>
            <Button
              variant="contained"
              style={{ 
                background: colors.primary,
                color: colors.white,
                borderRadius: 12
              }}
              onClick={handleCloseModal}
              sx={{ mt: 2 }}
              fullWidth
            >
              Entendi
            </Button>
          </Box>
        </Fade>
      </Modal>
    </Container>
  );
}